import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../../../context/analytics-context';
import { RotateControls } from '../../../../features/card-editor/components/rotate-controls/rotate-controls';
import { useActiveCanvas } from '../../../../hooks/use-active-canvas';
import { useActiveCardFace } from '../../../../hooks/use-active-card-face';
import { useCardContext } from '../../../../context/card-context';
import { usePhotoControls } from '../../../../features/card-editor/hooks/use-photo-controls';
import { getOrderIndices } from '../../../../utils/canvas-utils/get-order-indices';
import { HeaderContent as OrderHeaderContent } from '../../order-drawer/fragments/header-content';
import orderDrawerStyles from '../../order-drawer/order-drawer.module.scss';
import rotateDrawerStyles from '../../rotate-drawer/rotate-drawer.module.scss';
import { HeaderContent as ScaleHeaderContent } from '../../scale-drawer/fragments';
import scaleDrawerStyles from '../../scale-drawer/scale-drawer.module.scss';
import { useCropToolActions } from '../hooks/use-crop-tool-actions';
import { InlineToolBodyProps } from '../image-edit-toolbar-types';
import { applyOrderChange } from '../utils/apply-order-change';
import { applyRotateStep } from '../utils/apply-rotate-step';
import styles from './inline-tool-body.module.scss';

export const InlineToolBody = ({ tool }: InlineToolBodyProps): React.ReactElement => {
  const { t } = useTranslation();
  const canvas = useActiveCanvas();
  const { isFront } = useActiveCardFace();
  const { rotateSelectedPhoto } = usePhotoControls();
  const { updateEditFormats } = useAnalyticsContext();
  const { cardState } = useCardContext();
  const { cardFacesList, activeCardIndex } = cardState;
  const currentCardFace = cardFacesList[`${activeCardIndex}`];
  const isFrontFace = currentCardFace?.type === 'front' && currentCardFace?.active === true;
  const { onFinish: onFinishCropping } = useCropToolActions();

  return (
    <>
      {tool === 'scale' && (
        <div className={scaleDrawerStyles['scale-drawer']}>
          <ScaleHeaderContent
            onChangeCallback={(value) => updateEditFormats({ size: value.toString() })}
            minScale={isFront ? 100 : 10}
            scaleAtCenter={isFront ? undefined : 100}
            sliderCenter={isFront ? undefined : 100}
            mode={isFront ? 'left' : 'centered'}
          />
        </div>
      )}

      {tool === 'rotate' && (
        <div className={rotateDrawerStyles['rotate-drawer']}>
          <RotateControls
            onRotateLeft={() =>
              applyRotateStep({
                canvas: canvas?.current,
                activeObject: canvas?.current?.getActiveObject(),
                rotateRight: false,
                isFrontFace,
                rotateSelectedPhoto,
              })
            }
            onRotateRight={() =>
              applyRotateStep({
                canvas: canvas?.current,
                activeObject: canvas?.current?.getActiveObject(),
                rotateRight: true,
                isFrontFace,
                rotateSelectedPhoto,
              })
            }
          />
        </div>
      )}

      {tool === 'order' && (
        <div className={orderDrawerStyles['order-drawer']}>
          <OrderHeaderContent
            currentOrder={getOrderIndices(canvas?.current).currentIndex}
            handleOrderChange={(sliderValue: number) =>
              applyOrderChange({
                canvas: canvas?.current,
                sliderValue,
                onComplete: (newOrderIndex) => updateEditFormats({ order: newOrderIndex.toString() }),
              })
            }
          />
        </div>
      )}

      {tool === 'crop' && (
        // Back is provided by the toolbar header; body only renders Apply.
        <div className={styles['action-row']}>
          <Button
            variant="secondary"
            onClick={onFinishCropping}
            className={styles['apply-button']}
            data-testid="image-edit-toolbar-crop-apply"
          >
            {t('imageEditDrawer.applyCrop')}
          </Button>
        </div>
      )}
    </>
  );
};
